/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { GridCellProps, GridCustomCellProps } from '@progress/kendo-react-grid';
import { IntlService } from '@progress/kendo-react-intl';
import { LinkedCell } from './customCells/LinkedCell';
import DateCell from './customCells/DateCell';
import { ExtensionCell } from './customCells/ExtensionCell';
import { EditableNumericTextBoxCell } from './customCells/EditableNumericTextBoxCell';
import { CpsGridCell } from './customCells/CpsGridCell';
import DateTimeCell from './customCells/DateTimeCell';
import { BooleanCell } from './customCells/BooleanCell';
import { LockCell } from './customCells/LockedCell';
import { MultiChoiceCell } from './customCells/MultiChoiceCell';
import { ActionsCell } from './customCells/ActionsCell';
import { LinkCell } from './customCells/LinkCell';
import {
  CONTENT_TYPE_FIELD,
  CPS_FIELD_TYPE,
  CpsFieldForView,
  CpsFieldType,
  DOC_ID_FIELD,
  EMPTY_VALUE,
  EXTENSION_FIELD,
  LOCKED_FIELD,
  PERMISSIONS_FIELD
} from '@cs-cms/types';

export interface CpsGridCellRenderContext {
  columns: CpsFieldForView[] | undefined;
  intl: IntlService;
  openDocument: ((contentType: string, docId: string) => void) | undefined;
}

type CpsGridCellKind =
  | 'extension'
  | 'linked'
  | 'date'
  | 'link'
  | 'permissions'
  | 'multichoice'
  | 'datetime'
  | 'numeric'
  | 'boolean-lock'
  | 'boolean'
  | 'default';

const mapBaseCellProps = (cellProps: GridCellProps): GridCustomCellProps => ({
  dataItem: cellProps.dataItem,
  field: cellProps.field,
  dataIndex: cellProps.dataIndex,
  id: cellProps.id,
  columnPosition: cellProps.columnPosition,
  onChange: cellProps.onChange,
  className: cellProps.className,
  colSpan: cellProps.colSpan,
  ariaColumnIndex: cellProps.ariaColumnIndex,
  isSelected: cellProps.isSelected,
  style: cellProps.style,
  rowType: cellProps.rowType,
  level: cellProps.level,
  expanded: cellProps.expanded,
  tdProps: (cellProps as GridCustomCellProps).tdProps
});

const getCellType = (fieldName: string, columns: CpsFieldForView[] | undefined): CpsFieldType => {
  const column = columns?.find(field => field.name === fieldName);
  if (!column) {
    return CPS_FIELD_TYPE.TEXT;
  }
  return column.type;
};

const isLinkedDocumentField = (fieldName: string, columns: CpsFieldForView[] | undefined): boolean => {
  return Boolean(columns?.find(field => field.name === fieldName && field.isDocumentLink));
};

const resolveCellKind = (cellProps: GridCellProps, columns: CpsFieldForView[] | undefined): CpsGridCellKind | null => {
  const field = cellProps.field;
  if (!field) {
    return null;
  }

  if (field === EXTENSION_FIELD) {
    return 'extension';
  }

  if (field !== LOCKED_FIELD && isLinkedDocumentField(field, columns)) {
    return 'linked';
  }

  const cellType = getCellType(field, columns);

  if (cellType === CPS_FIELD_TYPE.DATE) {
    return 'date';
  }
  if (cellType === CPS_FIELD_TYPE.LINK) {
    return 'link';
  }
  if (field === PERMISSIONS_FIELD) {
    return 'permissions';
  }
  if (cellType === CPS_FIELD_TYPE.MULTICHOICE) {
    return 'multichoice';
  }
  if (cellType === CPS_FIELD_TYPE.DATETIME) {
    return 'datetime';
  }
  if (cellType === CPS_FIELD_TYPE.NUMERIC || cellType === CPS_FIELD_TYPE.INT32 || cellType === CPS_FIELD_TYPE.INT64) {
    return 'numeric';
  }
  if (cellType === CPS_FIELD_TYPE.BOOLEAN && field === LOCKED_FIELD) {
    return 'boolean-lock';
  }
  if (cellType === CPS_FIELD_TYPE.BOOLEAN) {
    return 'boolean';
  }

  return 'default';
};

const renderStandardCell = <P extends GridCustomCellProps>(
  CellComponent: React.ComponentType<P>,
  baseProps: GridCustomCellProps,
  extraProps?: Omit<P, keyof GridCustomCellProps>
): React.ReactElement => {
  return <CellComponent {...(baseProps as P)} {...(extraProps as P)} />;
};

interface LinkedCellDisplay {
  value: string | undefined;
  title: string | undefined;
}

const getLinkedCellDisplay = (
  field: string,
  dataItem: Record<string, unknown>,
  cellType: CpsFieldType,
  intl: IntlService
): LinkedCellDisplay => {
  const rawValue = dataItem[field];
  if (cellType === CPS_FIELD_TYPE.DATE) {
    const formatted = intl.formatDate(new Date(rawValue as string | number | Date));
    return {
      value: formatted,
      title: intl.formatDate(new Date(rawValue as string | number | Date), { date: 'full' })
    };
  }
  if (cellType === CPS_FIELD_TYPE.DATETIME) {
    const dateValue = new Date(rawValue as string | number | Date);
    return {
      value: intl.formatDate(dateValue, { datetime: 'short' }),
      title: intl.formatDate(dateValue, { date: 'full', datetime: 'medium' })
    };
  }
  if (cellType === CPS_FIELD_TYPE.NUMERIC) {
    const numericString = rawValue?.toString();
    return {
      value: numericString,
      title: numericString && !isNaN(Number(numericString)) ? numericString : ''
    };
  }

  const stringValue = rawValue?.toString();
  return { value: stringValue, title: stringValue };
};

const createOpenDocumentHandler = (
  dataItem: Record<string, unknown>,
  openDocument: CpsGridCellRenderContext['openDocument']
): (() => void) | undefined => {
  if (!openDocument) {
    return undefined;
  }
  return () => {
    const docId = dataItem[DOC_ID_FIELD] as string;
    const contentType = dataItem[CONTENT_TYPE_FIELD] as string;
    openDocument(contentType, docId);
  };
};

const renderLinkedCell = (
  cellProps: GridCellProps,
  baseProps: GridCustomCellProps,
  context: CpsGridCellRenderContext
): React.ReactElement => {
  const linkedCellField = cellProps.field || EMPTY_VALUE;
  const cellType = getCellType(linkedCellField, context.columns);
  const { value, title } = getLinkedCellDisplay(linkedCellField, cellProps.dataItem, cellType, context.intl);

  return (
    <LinkedCell
      {...baseProps}
      onLinkClicked={createOpenDocumentHandler(cellProps.dataItem, context.openDocument) ?? (() => undefined)}
      title={title?.toString()}
      value={value?.toString()}
      documentId={cellProps.dataItem[DOC_ID_FIELD] as string}
      documentName={value?.toString()}
      contentType={cellProps.dataItem[CONTENT_TYPE_FIELD] as string}
    />
  );
};

const renderLockCell = (
  baseProps: GridCustomCellProps,
  context: CpsGridCellRenderContext,
  dataItem: Record<string, unknown>
): React.ReactElement => {
  const isLinkedLockField = Boolean(
    context.columns?.find(field => field.name === LOCKED_FIELD && field.isDocumentLink)
  );
  const onLinkClicked = isLinkedLockField ? createOpenDocumentHandler(dataItem, context.openDocument) : undefined;

  return <LockCell {...baseProps} linked={isLinkedLockField} onLinkClicked={onLinkClicked} />;
};

const renderDefaultCell = (cellProps: GridCellProps, baseProps: GridCustomCellProps): React.ReactElement => {
  const field = cellProps.field ?? '';
  const cellValue = cellProps.dataItem[field] || '';

  return (
    <CpsGridCell {...baseProps} title={cellValue}>
      {cellValue}
    </CpsGridCell>
  );
};

const renderCellByKind = (
  kind: CpsGridCellKind,
  cellProps: GridCellProps,
  baseProps: GridCustomCellProps,
  context: CpsGridCellRenderContext
): React.ReactElement => {
  switch (kind) {
    case 'extension':
      return renderStandardCell(ExtensionCell, baseProps);
    case 'linked':
      return renderLinkedCell(cellProps, baseProps, context);
    case 'date':
      return renderStandardCell(DateCell, baseProps);
    case 'link':
      return renderStandardCell(LinkCell, baseProps);
    case 'permissions':
      return renderStandardCell(ActionsCell, baseProps);
    case 'multichoice':
      return renderStandardCell(MultiChoiceCell, baseProps);
    case 'datetime':
      return renderStandardCell(DateTimeCell, baseProps);
    case 'numeric':
      return renderStandardCell(EditableNumericTextBoxCell, baseProps, {
        fieldType: getCellType(cellProps.field ?? '', context.columns),
        inEdit: false
      });
    case 'boolean-lock':
      return renderLockCell(baseProps, context, cellProps.dataItem);
    case 'boolean':
      return renderStandardCell(BooleanCell, baseProps);
    default:
      return renderDefaultCell(cellProps, baseProps);
  }
};

export const createCpsGridCellRender = (
  context: CpsGridCellRenderContext
): ((cellProps: GridCellProps) => React.ReactNode) => {
  return (cellProps: GridCellProps): React.ReactNode => {
    const kind = resolveCellKind(cellProps, context.columns);
    if (!kind) {
      return null;
    }

    const baseProps = mapBaseCellProps(cellProps);
    return renderCellByKind(kind, cellProps, baseProps, context);
  };
};
